import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../api/api";
import {
  Pencil,
  Upload,
  Download,
  FileText,
  Briefcase,
  ShoppingBag,
  DollarSign,
  Clock,
  ShieldCheck,
  X,
  Ban,
  RotateCcw,
  FileEdit,
  Check,
  Calculator,
  Wallet,
  CreditCard
} from "lucide-react";
import AddDealFinanceDrawer from "./AddDealFinance";
import AddDealPurchaseDrawer from "./AddDealPurchase";
import { useNavigate } from "react-router-dom";

// Formats a numeric amount using the Indian digit-grouping system (e.g. 1234567 -> "12,34,567")
function formatIndianCurrency(value) {
  if (value === null || value === undefined || value === "") return "";
  const sanitized = String(value).replace(/[^0-9.]/g, "");
  if (sanitized === "") return "";

  const [integerPartRaw, ...decimalParts] = sanitized.split(".");
  const decimalPart = decimalParts.length ? decimalParts.join("") : undefined;
  const integerPart = integerPartRaw.replace(/^0+(?=\d)/, "") || "0";

  let formattedInteger;
  if (integerPart.length <= 3) {
    formattedInteger = integerPart;
  } else {
    const lastThree = integerPart.slice(-3);
    const remaining = integerPart.slice(0, -3);
    formattedInteger = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
  }

  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
}

// Strips comma separators back out, returning the raw numeric string for state/submission
function unformatCurrency(value) {
  if (value === null || value === undefined) return "";
  return String(value).replace(/,/g, "");
}

// Shipping addresses are stored as a single packed string on the backend
// (see AddDealSales.jsx, where they're created): Name / Contact / Email /
// Address / GST joined together, with each field's own label substituted
// in when that field was left blank. These helpers mirror that scheme so
// the packed string can be unpacked into editable fields here, and packed
// back the same way before saving.
const ADDRESS_SEPARATOR = "🔸";
const LEGACY_ADDRESS_SEPARATORS = [" 🔸 ", "//", "||"];
const ADDRESS_FIELD_LABELS = ["Name", "Contact", "Email", "Address", "GST"];

function splitPackedAddress(packed) {
  const str = String(packed ?? "");
  const sep = str.includes(ADDRESS_SEPARATOR)
    ? ADDRESS_SEPARATOR
    : LEGACY_ADDRESS_SEPARATORS.find((s) => str.includes(s)) || ADDRESS_SEPARATOR;
  const parts = str.split(sep);
  const unfallback = (v, i) => (v === ADDRESS_FIELD_LABELS[i] ? "" : v || "");
  return {
    name: unfallback(parts[0], 0),
    contact: unfallback(parts[1], 1),
    email: unfallback(parts[2], 2),
    addressLine: unfallback(parts[3], 3),
    gst: unfallback(parts[4], 4),
  };
}

function packAddress(a) {
  const values = [a.name, a.contact, a.email, a.addressLine, a.gst];
  return values
    .map((v, i) => (v && String(v).trim() ? v : ADDRESS_FIELD_LABELS[i]))
    .join(ADDRESS_SEPARATOR);
}

// The shipping address primary key shows up under different names
// depending on how the row was queried - mirrors the same fallback used
// in getShippingAddressForLine below, kept in one place so both agree.
function getAddressId(addr) {
  return addr?.shipping_id ?? addr?.opfsa_id ?? addr?.id ?? addr?.address_id;
}

// Used for dynamically-rendered records (purchase orders, payment in/out
// entries) where field keys come straight from the API instead of being
// hand-labeled — flags keys that represent a money amount so they get the
// ₹ prefix and Indian comma grouping, while leaving ids/dates/percentages/
// text fields alone.
function isCurrencyField(key) {
  const k = String(key).toLowerCase();
  if (/(pct|percent|_id$|^id$|number|date|name|code|email|phone|address|status|term|remark|attachment|mode|mime|filename|type)/.test(k)) {
    return false;
  }
  return /(value|price|amount|charge|fee|margin|credit|advance|balance|tds|emd|guarantee|tax)/.test(k);
}

export default function DealDetails() {
  const navigate = useNavigate();
const { id: opfId } = useParams();
  const [openPurchaseModal, setPurchaseModal] = useState(false);
  const [openFinanceModal, setFinanceModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const storedUser = localStorage.getItem("user");
const user = storedUser ? JSON.parse(storedUser) : null;
const [opfData, setOpfData] = useState(null);
const [productLines, setProductLines] = useState([]);
const [shippingAddresses, setShippingAddresses] = useState([]);
const [leadData, setLeadData] = useState(null);
const [customerData, setCustomerData] = useState(null);
const currentRole = (user?.department || "guest").toLowerCase();

const [purchaseMode, setPurchaseMode] = useState(false);
const [purchaseOrders, setPurchaseOrders] = useState([]);
const [openAddPurchaseModal, setOpenAddPurchaseModal] = useState(false);

const [paymentInMode, setPaymentInMode] = useState(false);
const [paymentOutMode, setPaymentOutMode] = useState(false);
const [paymentInEntries, setPaymentInEntries] = useState([]);
const [paymentOutEntries, setPaymentOutEntries] = useState([]);

  // --- NEW STATE FOR EDIT MODES ---
  const [isEditingSales, setIsEditingSales] = useState(false);
  const [isEditingPurchase, setIsEditingPurchase] = useState(false);
  const [isEditingFinance, setIsEditingFinance] = useState(false);

  // --- Shipping address edit mode: only the address fields become
  // editable, everything else on the page stays read-only ---
  const [isEditingAddresses, setIsEditingAddresses] = useState(false);
  const [addressDrafts, setAddressDrafts] = useState([]);
  const [savingAddresses, setSavingAddresses] = useState(false);
  const [addressSaveError, setAddressSaveError] = useState("");

  const startEditingAddresses = () => {
    setAddressSaveError("");
    setAddressDrafts(
      shippingAddresses.map((addr) => ({
        shipping_id: getAddressId(addr),
        label: addr.label || "",
        ...splitPackedAddress(addr.address),
      }))
    );
    setIsEditingAddresses(true);
  };

  const cancelEditingAddresses = () => {
    setIsEditingAddresses(false);
    setAddressDrafts([]);
    setAddressSaveError("");
  };

  const updateAddressDraft = (shippingId, key, value) => {
    setAddressDrafts((prev) =>
      prev.map((d) => (d.shipping_id === shippingId ? { ...d, [key]: value } : d))
    );
  };

  const saveAddressEdits = async () => {
    try {
      setSavingAddresses(true);
      setAddressSaveError("");

      const missingId = addressDrafts.find((d) => d.shipping_id === undefined || d.shipping_id === null);
      if (missingId) {
        setAddressSaveError(
          `Couldn't determine an ID for "${missingId.label || "one of the addresses"}" - it wasn't saved. ` +
            "Check that the shipping address rows returned by the API include an id field."
        );
        return;
      }

      await Promise.all(
        addressDrafts.map((d) =>
          api.put(`/deal-details/update_shipping_address/${d.shipping_id}`, {
            label: d.label,
            address: packAddress(d),
          })
        )
      );

      setShippingAddresses((prev) =>
        prev.map((addr) => {
          const draft = addressDrafts.find((d) => d.shipping_id === getAddressId(addr));
          return draft ? { ...addr, label: draft.label, address: packAddress(draft) } : addr;
        })
      );

      setIsEditingAddresses(false);
      setAddressDrafts([]);
    } catch (err) {
      console.error("Failed to update shipping addresses", err);
      const serverMessage = err.response?.data?.message;
      const statusCode = err.response?.status;
      if (statusCode && serverMessage) {
        setAddressSaveError(`Failed to save (${statusCode}): ${serverMessage}`);
      } else if (statusCode) {
        setAddressSaveError(`Failed to save shipping address changes (server returned ${statusCode}).`);
      } else {
        setAddressSaveError(
          "Failed to save shipping address changes: could not reach the server. Check your connection and try again."
        );
      }
    } finally {
      setSavingAddresses(false);
    }
  };

  /*******************************
   * Role Permissions
   *******************************/
 const permissions = {
  sales: {
    sales: { view: true, edit: true },
    purchase: { view: false, edit: false },
    finance: { view: false, edit: false },
  },

  purchase: {
    sales: { view: true, edit: false },
    purchase: { view:true, edit: true },
    finance: { view: false, edit: false },
  },

  finance: {
    sales: { view: true, edit: false },
    purchase: { view: true, edit: false },
    finance: { view: true, edit: true },
  },

  admin: {
    sales: { view: true, edit: true },
    purchase: { view: false, edit: false },
    finance: { view: false, edit: false },
  },

  guest: {
    sales: { view: false, edit: false },
    purchase: { view: false, edit: false },
    finance: { view: false, edit: false },
  },};

const access = permissions[currentRole] || permissions.guest;

  /*******************************
   * Tabs
   *******************************/
  const [activeTab, setActiveTab] = useState("All Fields");
  const [newNote, setNewNote] = useState("");

  /*******************************
   * Notes & Attachments 
   *******************************/
  const [notes, setNotes] = useState([]);
const [files, setFiles] = useState([]);
const [uploading, setUploading] = useState(false);

  /*******************************
   * Deal Data
   *******************************/
  const [dealData, setDealData] = useState({
    uniqueId: "",
customerName: "",
accountManager: "",
    description: "Enterprise network infrastructure implementation and software license renewal.",
    sales: {
  customerPoNumber: "",
  poDate: "",
  poValue: "",
  tax: "",
  warrantyValidity: "",
  otherBudgetEstimation: "",
  gstNumber: "",

  paymentTerms: "",
  emd: "",
  bankGuarantee: "",

  contactDetails: "",
  deliveryDetails: "",
  licenseKey: "",
},
    purchase: {
  f1PoNumber: "",
  f1PoDate: "",
  tp: "",
  otherCredits: "",
  creditsRemark: "",
  f1PoValue: "",
  distiName: "",
  distiInvoiceNumber: "",
  distiInvoiceDate: "",
  distiInvoiceValueIncTax: "",
  tax: "",
  attachment: null,
  deliveryDetails: "",
  licenseKey: "",
  distributorInvoiceAttachment: null,
  otherTransportExpense: "",
},
    finance: {
      f1InvoiceNumber: "F1-INV-2026-042", f1InvoiceDate: "2026-04-25", totalValue: "₹14,75,000",
      customerAdvanceReceived: "₹4,00,000", customerBalance: "₹10,75,000",
      customerPartFinalPayment: "Part Payment Received", customerTds: "₹14,750", chequeNumber: "CHQ-992817",
      chequeDate: "2026-05-10",
    },
  });
  useEffect(() => {
  const fetchDealHeader = async () => {
    try {
      const res = await api.get(`/deal-details/${opfId}`);

      const data = res.data.data;

      setDealData(prev => ({
        ...prev,
        uniqueId: opfId,
        customerName: data.customer_name || "",
        accountManager: data.account_manager || ""
      }));

      setContacts(data.point_of_contacts || []);
    } catch (err) {
      console.error("Failed to fetch deal details", err);
    }
  };

  if (opfId) {
    fetchDealHeader();
  }
}, [opfId]);

useEffect(() => {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) return;

  try {
    const user = JSON.parse(storedUser);
    const department = (user.department || "guest").toLowerCase();

    const viewMode =
      localStorage.getItem("dealViewMode") || department;

    setPurchaseMode(viewMode === "purchase");
    setPaymentInMode(viewMode === "payment-in");
    setPaymentOutMode(viewMode === "payment-out");
  } catch (err) {
    console.error("Failed to parse user", err);
  }
}, []);



useEffect(() => {
  if (!purchaseMode || !opfId) return;

  const fetchPurchaseOrders = async () => {
    try {
      const res = await api.get(`/purchase/list/${opfId}`);
      setPurchaseOrders(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  fetchPurchaseOrders();
}, [purchaseMode, opfId]);

useEffect(() => {
  if (!paymentInMode || !opfId) return;

  const fetchPaymentInEntries = async () => {
    try {
      const res = await api.get(`/payment-in/list/${opfId}`);
      setPaymentInEntries(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  fetchPaymentInEntries();
}, [paymentInMode, opfId]);

useEffect(() => {
  if (!paymentOutMode || !opfId) return;

  const fetchPaymentOutEntries = async () => {
    try {
      const res = await api.get(`/payment-out/list/${opfId}`);
      setPaymentOutEntries(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  fetchPaymentOutEntries();
}, [paymentOutMode, opfId]);

useEffect(() => {
  if (!opfId) return;

  const fetchAllData = async () => {
    try {
      const res = await api.get(`/deal-details/all/${opfId}`);

      const data = res.data.data;

      console.log("DEAL DATA:", data);


      setOpfData(data.opf);
      setLeadData(data.lead);
      setCustomerData(data.customer);

      setProductLines(data.product_lines || []);
      setShippingAddresses(data.shipping_addresses || []);


      setDealData(prev => ({
        ...prev,

        uniqueId: data.opf?.opf_number || "",
        customerName: data.customer?.cust_name || "",


        sales: {
          ...prev.sales,

          customerPoNumber:
            data.opf?.po_no || "",

          poValue:
            data.opf?.gem_tender_po_value || "",

          paymentTerms:
            data.opf?.customer_payment_terms || "",

          deliveryDetails:
            data.opf?.delivery_terms || "",


          contactDetails:
            `${data.opf?.end_customer_contact || ""} 
             ${data.opf?.phone || ""} 
             ${data.opf?.email || ""}`
        },


        purchase: {
          ...prev.purchase,

          distiName:
            data.opf?.distributor_name || ""
        },


        finance:{
          ...prev.finance,

          totalValue:
            data.opf?.gem_tender_po_value || ""
        }

      }));


    } catch(err){
      console.error(
        "Failed loading deal details",
        err
      );
    }
  };


  fetchAllData();

},[opfId]);


useEffect(() => {
  if (!opfId) return;

  const fetchNotes = async () => {
    try {
      const res = await api.get(`/deal-details/read_notes/${opfId}`);
      setNotes(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAttachments = async () => {
    try {
      const res = await api.get(`/deal-details/read_attachments/${opfId}`);
      setFiles(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  fetchNotes();
  fetchAttachments();
}, [opfId]);

  const activities = [
    { title: "Advance Payment Logged", owner: "Finance", time: "2 Days Ago" },
    { title: "Distributor Invoice Uploaded", owner: "Purchase", time: "3 Days Ago" },
    { title: "Deal Created", owner: "Sales", time: "15 Apr 2026" },
  ];

  const updateField = (section, field, value) => {
    setDealData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };
const saveSalesContext = async () => {
  try {
    const payload = {
      opf_id: opfId,

      opf_cust_po_number: dealData.sales.customerPoNumber,
      opf_cust_po_date: dealData.sales.poDate,
      opf_cust_po_value: dealData.sales.poValue,
      opf_cust_tax: dealData.sales.tax,

      opf_warranty_validity: dealData.sales.warrantyValidity,
      opf_other_budget_estimation: dealData.sales.otherBudgetEstimation,

      opf_payment_terms: dealData.sales.paymentTerms,
      opf_emd: dealData.sales.emd,
      opf_bank_guarantee: dealData.sales.bankGuarantee,

      opf_contact_details: dealData.sales.contactDetails,
      opf_delivery_details: dealData.sales.deliveryDetails,
      opf_licenseKey: dealData.sales.licenseKey
    };


    await api.put(
      `/deal-details/edit_sales_context/${opfId}`,
      payload
    );

    setIsEditingSales(false);

    alert("Sales details updated successfully");

  } catch (err) {
    console.error("Failed to update sales details", err);
    alert("Failed to update sales details");
  }
};
 const addNote = async () => {
  if (!newNote.trim()) return;

  try {
    await api.post("/deal-details/add_notes", {
      opf_id: opfId,
      note: newNote,
    });

    setNewNote("");

    const res = await api.get(`/deal-details/read_notes/${opfId}`);
    setNotes(res.data.data || []);
  } catch (err) {
    console.error(err);
  }
};

  const uploadAttachment = async (e) => {
  const file = e.target.files[0];

  if (!file) return;

  const formData = new FormData();

  formData.append("opf_id", opfId);
  formData.append("attachment", file);

  try {
    setUploading(true);

    await api.post(
      "/deal-details/add_attachment",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    const res = await api.get(
      `/deal-details/read_attachments/${opfId}`
    );

    setFiles(res.data.data || []);
  } catch (err) {
    console.error(err);
  } finally {
    setUploading(false);
  }
};
const downloadAttachment = async (attachmentId, fileName) => {
  try {
    const res = await api.get(
      `/deal-details/download_attachment/${attachmentId}`,
      {
        responseType: "blob",
      }
    );

    const url = window.URL.createObjectURL(new Blob([res.data]));

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Download failed", err);
  }
};
// Maps a MIME type to a fallback extension, only used if the stored
// filename has none (older rows saved before metadata was tracked).
const extensionFromMime = (mime) => {
  const map = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  };
  return map[mime] || "bin";
};

const downloadBase64File = (base64, filenameBase, mimeType, originalFilename) => {
  if (!base64) return;

  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);

  const blob = new Blob([byteArray], {
    type: mimeType || "application/octet-stream",
  });

  const url = window.URL.createObjectURL(blob);

  // Prefer the original filename (has the correct extension already).
  // Fall back to filenameBase + an extension guessed from the mime type
  // for older records saved before we tracked filename/mime.
  const finalName =
    originalFilename || `${filenameBase}.${extensionFromMime(mimeType)}`;

  const a = document.createElement("a");
  a.href = url;
  a.download = finalName;

  document.body.appendChild(a);
  a.click();

  a.remove();
  window.URL.revokeObjectURL(url);
};

const tabs = [
  {
    label: "All Fields",
    visible: !purchaseMode && !paymentInMode && !paymentOutMode,
  },
  {
    label: "OPF Details",
    visible: !purchaseMode && !paymentInMode && !paymentOutMode,
  },
  {
    label: "Opportunity Details",
    visible: !purchaseMode && !paymentInMode && !paymentOutMode,
  },
  {
    label: "Customer Details",
    visible: !purchaseMode && !paymentInMode && !paymentOutMode,
  },
];

  const schedulePayment = () => {
  alert("Schedule Payment clicked");
};

  // Product lines are linked to a shipping address via a foreign key on the
  // product line row. The exact column name can vary depending on how the
  // table was created, so we check the common possibilities on both sides
  // before falling back to "not assigned".
  const getShippingAddressForLine = (item) => {
    const lineAddressId =
      item.shipping_address_id ??
      item.opf_shipping_address_id ??
      item.shipping_id ??
      item.opfsa_id ??
      item.address_id;

    if (lineAddressId === undefined || lineAddressId === null || lineAddressId === "") {
      return null;
    }

    return (
      shippingAddresses.find((addr) => {
        const addrId =
          addr.shipping_id ??
          addr.opfsa_id ??
          addr.id ??
          addr.address_id;
        // Loose equality so string/number id mismatches (e.g. "1" vs 1) still match
        return addrId != null && String(addrId) === String(lineAddressId);
      }) || null
    );
  };

  return (
    <div className="ml-[60px] pt-16 p-3 sm:p-6 bg-[#F4F7FB] min-h-screen  mt-12">
      {/* HEADER */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6 mb-6 flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
  <div className="flex items-center gap-3">
    {/* <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-md">
      Deal ID : {dealData.uniqueId}
    </span> */}

    <h1 className="text-2xl font-semibold text-gray-800">
      {dealData.customerName}
    </h1>
  </div>

  <p className="mt-2 text-sm text-gray-500">
    Opportunity ID :
    <span className="font-medium text-blue-700 ml-1">
      {leadData?.ld_id || localStorage.getItem("lead_id") || "—"}
    </span>
  </p>

  <p className="mt-1 text-sm text-gray-500">
    Account Manager :
    <span className="font-medium text-gray-700 ml-1">
      {dealData.accountManager}
    </span>
  </p>
</div>

        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          {/* {access.sales.edit && (
            <button className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium">
              <Ban size={15} /> Cancel Deal
            </button>
          )} */}
         {/* {access.sales.edit && (
            <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium">
              <FileEdit size={15} /> Amend
            </button>
          )}*/}
          {access.purchase.edit && (
  <button
    onClick={() => navigate(`/add_purchase/${opfId}`)}
    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
  >
    <Upload size={15} />
    Upload F1 PO or Take Action
  </button>
)}
          {access.finance.edit && (
            <>
              <button
                onClick={() => navigate(`/manage_payment/${opfId}`)}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                <Wallet size={15} /> Manage Payment
              </button>

              <button
                onClick={() => navigate(`/manage_invoice/${opfId}`)}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                <FileText size={15} /> Manage Invoice
              </button>
            </>
          )}
          
          <AddDealFinanceDrawer isOpen={openFinanceModal} onClose={() => setFinanceModal(false)} />
          <AddDealPurchaseDrawer isOpen={openPurchaseModal} onClose={() => setPurchaseModal(false)} />
        </div>
      </div>

      {/* Tabs */}
      {/* Tabs */}
{!purchaseMode && !paymentInMode && !paymentOutMode && (
  <div className="sticky top-16 z-30 bg-[#F4F7FB] pb-2 mb-6">
    <div className="flex overflow-x-auto border-b border-gray-200 bg-[#F4F7FB]">
      {tabs
        .filter((tab) => tab.visible)
        .map((tab) => (
          <button
            key={tab.label}
            onClick={() => setActiveTab(tab.label)}
            className={`shrink-0 whitespace-nowrap px-4 sm:px-5 py-3 text-sm font-medium transition ${
              activeTab === tab.label
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            {tab.label}
          </button>
        ))}
    </div>
  </div>
)}
       
      <div
  className={`grid gap-6 ${
    (purchaseMode || paymentInMode || paymentOutMode)
      ? "grid-cols-1"
      : "lg:grid-cols-[68%_32%]"
  }`}
>
        {/* LEFT COLUMN */}
        <div className="space-y-6">
          
          {/* SALES CARD */}
          
         {!purchaseMode && !paymentInMode && !paymentOutMode &&
 (activeTab === "All Fields" || activeTab === "OPF Details") && (
  <>
    <RoleCard
      title="Basic Details"
      icon={<Briefcase size={18} />}
      accentColor="border-t-blue-600"
    >
      <div className="grid md:grid-cols-3 gap-5">

        {/* <Field
          label="OPF Number"
          value={opfData?.opf_number}
        /> */}

        <Field
          label="PO Number"
          value={opfData?.po_no}
        />

        <Field
          label="Status"
          value={opfData?.status}
        />

        <Field
          label="Customer Payment Terms"
          value={opfData?.customer_payment_terms}
        />

        <Field
          label="Vendor Payment Terms"
          value={opfData?.vendor_payment_terms}
        />

        <Field
          label="Delivery Terms"
          value={opfData?.delivery_terms}
        />

        <Field
          label="Dropshipment"
          value={opfData?.dropshipment}
        />

        <Field
          label="Distributor Name"
          value={opfData?.distributor_name}
        />

        <Field
          label="Implementation Location"
          value={opfData?.location_implementation}
        />

        <Field
          label="Location for Onsite Support"
          value={opfData?.location_onsite_support}
        />

        <Field
          label="Product Summary"
          value={opfData?.product_summary}
        />

        <Field
          label="Billing Address (with GST)"
          value={opfData?.billing_address}
        />

        <Field
          label="End Customer Contact Person"
          value={opfData?.end_customer_contact}
        />

        <Field
          label="Phone"
          value={opfData?.phone}
        />

        <Field
          label="Email"
          value={opfData?.email}
        />

      </div>
    </RoleCard>


    <RoleCard
  title="Product Details"
  icon={<Briefcase size={18} />}
  accentColor="border-t-purple-600"
>

  <div className="space-y-3">

    {productLines.map((item) => {
      const lineShippingAddress = getShippingAddressForLine(item);

      return (
      <div
        key={item.line_id}
        className="border rounded-lg p-4 grid md:grid-cols-3 gap-3"
      >

        <Field
          label="OEM"
          value={item.oem}
        />

        <Field
          label="Product"
          value={item.product}
        />

        <Field
          label="Qty"
          value={item.qty}
        />

        <Field
          label="Unit Price"
          value={item.unit_price}
          isCurrency
        />

        <Field
          label="Total PO Value"
          value={item.total_po_value}
          isCurrency
        />

        <Field
          label="Vendor Unit Price"
          value={item.vendor_unit_price}
          isCurrency
        />

        <Field
          label="Vendor Total PO Value"
          value={item.total_po_value_vendor}
          isCurrency
        />

        <Field
          label="Shipping Address"
          value={
            lineShippingAddress
              ? `${lineShippingAddress.label ? lineShippingAddress.label + " — " : ""}${lineShippingAddress.address || ""}`
              : ""
          }
          className="md:col-span-2"
          large
        />

      </div>
      );
    })}

  </div>

</RoleCard>
{/* CHARGES & NET MARGIN */}
{(activeTab === "All Fields" || activeTab === "OPF Details") && (
  <RoleCard
    title="Charges & Net Margin"
    icon={<Calculator size={18} />}
    accentColor="border-t-red-500"
  >
    <div className="grid md:grid-cols-3 gap-5">

      <Field
        label="Project Management Charges"
        value={opfData?.project_management_charges}
        isCurrency
      />

      <Field
        label="Misc Charges (10% of PM Charges)"
        value={opfData?.misc_charges}
        isCurrency
      />

      <Field
        label='Implementation Charges (E)'
        value={opfData?.implementation_charges}
        isCurrency
      />

      <Field
        label="Post Implementation Support Charges (F)"
        value={opfData?.post_implementation_support_charges}
        isCurrency
      />

      <Field
        label="Cost of Finance of PBG (G)"
        value={opfData?.cost_of_finance_pbg}
        isCurrency
      />

      <Field
        label="Cost of Finance (1 Year)"
        value={opfData?.cost_finance_1yr}
        isCurrency
      />

      <Field
        label="Cost of Finance (2 Years)"
        value={opfData?.cost_finance_2yr}
        isCurrency
      />

      <Field
        label="Cost of Finance (3 Years)"
        value={opfData?.cost_finance_3yr}
        isCurrency
      />

      <Field
        label="Logistics & Insurance (I)"
        value={opfData?.logistic_insurance}
        isCurrency
      />

      <Field
        label="Distributor Margin (0.5% of Vendor PO Value)"
        value={opfData?.distributor_margin}
        isCurrency
      />

      <Field
        label="End User PO Value"
        value={opfData?.end_user_po_value}
        isCurrency
      />

      <Field
        label="GEM Tender PO Value Including GST"
        value={opfData?.gem_tender_po_value}
        isCurrency
      />

      <Field
        label="GEM Fees (0.5% of GEM Tender PO Value)"
        value={opfData?.gem_fees}
        isCurrency
      />

      <Field
        label="Vendor PO Value"
        value={opfData?.vendor_po_value}
        isCurrency
      />

      <Field
        label="Gross Margin"
        value={opfData?.gross_margin}
        isCurrency
      />

      <Field
        label="Gross Margin %"
        value={opfData?.gross_margin_pct}
      />

      <Field
        label="Net Margin"
        value={opfData?.net_margin}
        isCurrency
      />

    </div>
  </RoleCard>
)}


    <Card
      title="Shipping Addresses"
      action={
        access.sales.edit &&
        shippingAddresses.length > 0 &&
        (isEditingAddresses ? (
          <div className="flex items-center gap-2">
            <button
              onClick={saveAddressEdits}
              disabled={savingAddresses}
              className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white px-3 py-1.5 rounded-md text-xs font-medium"
            >
              <Check size={13} /> {savingAddresses ? "Saving..." : "Save"}
            </button>
            <button
              onClick={cancelEditingAddresses}
              disabled={savingAddresses}
              className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 disabled:opacity-60 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium"
            >
              <X size={13} /> Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={startEditingAddresses}
            className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-600 px-3 py-1.5 rounded-md text-xs font-medium"
          >
            <Pencil size={13} /> Edit
          </button>
        ))
      }
    >

      {addressSaveError && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
          {addressSaveError}
        </div>
      )}

      {shippingAddresses.length > 0 ? (
        isEditingAddresses ? (
          addressDrafts.map((draft) => (
            <ShippingAddressEditForm
              key={draft.shipping_id}
              draft={draft}
              onChange={(key, value) => updateAddressDraft(draft.shipping_id, key, value)}
            />
          ))
        ) : (
          shippingAddresses.map((addr) => (
            <ShippingAddressView key={getAddressId(addr)} addr={addr} />
          ))
        )
      ) : (
        <p className="text-sm text-gray-500">
          No shipping addresses available.
        </p>
      )}

    </Card>

  </>
)}

          {/* PURCHASE CARD */}
         {!purchaseMode && !paymentInMode && !paymentOutMode &&
 (activeTab === "All Fields" || activeTab === "Opportunity Details") && (
           <RoleCard
title="Opportunity Details"
icon={<ShoppingBag size={18}/>}
accentColor="border-t-amber-500"
>

<div className="grid md:grid-cols-3 gap-5">


<Field
label="Opportunity ID"
value={leadData?.ld_id}
/>


<Field
label="Source"
value={leadData?.source}
/>


<Field
label="Status"
value={leadData?.status}
/>

<Field
label="Amount"
value={leadData?.ld_estimated_budget}
isCurrency
/>


{/* <Field
label="Priority"
value={leadData?.priority}
/> */}


<Field
label="Level"
value={leadData?.level}
/>


<Field
label="GST"
value={leadData?.ld_gst}
/>


<Field
label="CIN"
value={leadData?.ld_cin}
/>


<Field
label="Address"
value={leadData?.ld_address}
className="md:col-span-3"
/>


<Field
label="City"
value={leadData?.ld_city}
/>


<Field
label="State"
value={leadData?.ld_state}
/>


<Field
label="Pincode"
value={leadData?.ld_pincode}
/>


</div>


</RoleCard>
          )}

          {/* FINANCE CARD */}
         {!purchaseMode && !paymentInMode && !paymentOutMode &&
 (activeTab === "All Fields" || activeTab === "Customer Details") && (
            <RoleCard
title="Customer Details"
icon={<DollarSign size={18}/>}
accentColor="border-t-green-600"
>

<div className="grid md:grid-cols-3 gap-5">


<Field
label="Customer Name"
value={customerData?.cust_name}
/>


<Field
label="GST"
value={customerData?.cust_gst}
/>


<Field
label="CIN"
value={customerData?.cust_cin}
/>


<Field
label="Address"
value={customerData?.cust_address}
/>


<Field
label="City"
value={customerData?.cust_city}
/>


<Field
label="State"
value={customerData?.cust_state}
/>


<Field
label="Pincode"
value={customerData?.cust_pincode}
/>


</div>

</RoleCard>
          )}
          {purchaseMode && (
  <>
    {purchaseOrders.length === 0 ? (
      <Card title="Purchase Orders">
        <div className="text-center text-gray-500 py-10">
          Add a purchase order to view them here.
        </div>
      </Card>
    ) : (
      purchaseOrders.map((purchase) => (
        <RoleCard
          key={purchase.purchase_id}
          title={`Purchase Order #${purchase.purchase_id}`}
          icon={<ShoppingBag size={18} />}
          accentColor="border-t-blue-600"
        >
          <div className="grid md:grid-cols-3 gap-5">

            {/* All normal fields first */}
{Object.entries(purchase)
  .filter(
    ([key]) =>
      key !== "attachment" &&
      key !== "attachment_filename" &&
      key !== "attachment_mime" &&
      key !== "distributor_invoice_attachment" &&
      key !== "distributor_invoice_attachment_filename" &&
      key !== "distributor_invoice_attachment_mime"
  )
  .map(([key, value]) => (
    <Field
      key={key}
      label={key.replace(/_/g, " ")}
      value={value}
      isCurrency={isCurrencyField(key)}
    />
  ))}

{/* F1 PO Attachment */}
<div className="flex flex-col gap-1">
  <label className="text-xs font-medium text-gray-500">
    F1 PO Attachment
  </label>

  {purchase.attachment ? (
    <button
      onClick={() =>
        downloadBase64File(
          purchase.attachment,
          `F1_PO_${purchase.purchase_id}`,
          purchase.attachment_mime,
          purchase.attachment_filename
        )
      }
      className="flex items-center justify-center gap-2 border rounded-md px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600"
    >
      <Download size={16} />
      Download
    </button>
  ) : (
    <div className="border rounded-md px-3 py-2 bg-gray-50 text-sm text-gray-500">
      —
    </div>
  )}
</div>

{/* Distributor Invoice Attachment */}
<div className="flex flex-col gap-1">
  <label className="text-xs font-medium text-gray-500">
    Distributor Invoice Attachment
  </label>

  {purchase.distributor_invoice_attachment ? (
    <button
      onClick={() =>
        downloadBase64File(
          purchase.distributor_invoice_attachment,
          `Distributor_Invoice_${purchase.purchase_id}`,
          purchase.distributor_invoice_attachment_mime,
          purchase.distributor_invoice_attachment_filename
        )
      }
      className="flex items-center justify-center gap-2 border rounded-md px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600"
    >
      <Download size={16} />
      Download
    </button>
  ) : (
    <div className="border rounded-md px-3 py-2 bg-gray-50 text-sm text-gray-500">
      —
    </div>
  )}
</div>

          </div>
          <div className="mt-6 flex justify-end">
  <button
    onClick={() => navigate(`/schedule_payment/${purchase.purchase_id}`)}
    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-md text-sm font-medium"
  >
    <DollarSign size={16} />
    Schedule Payment
  </button>
</div>
        </RoleCard>
      ))
    )}
  </>
)}

          {paymentInMode && (
  <>
    {paymentInEntries.length === 0 ? (
      <Card title="Payment In">
        <div className="text-center text-gray-500 py-10">
          No payment-in entries recorded yet.
        </div>
      </Card>
    ) : (
      paymentInEntries.map((payment) => (
        <RoleCard
          key={payment.payment_id}
          title={`Payment In #${payment.payment_id}`}
          icon={<Wallet size={18} />}
          accentColor="border-t-green-600"
        >
          <div className="grid md:grid-cols-3 gap-5">

            {/* All normal fields first */}
{Object.entries(payment)
  .filter(([key]) => key !== "attachment")
  .map(([key, value]) => (
    <Field
      key={key}
      label={key.replace(/_/g, " ")}
      value={value}
      isCurrency={isCurrencyField(key)}
    />
  ))}

{/* Attachment */}
<div className="flex flex-col gap-1">
  <label className="text-xs font-medium text-gray-500">
    Attachment
  </label>

  {payment.attachment ? (
    <button
      onClick={() =>
        downloadBase64File(
          payment.attachment,
          `Payment_In_${payment.payment_id}`,
          "application/octet-stream"
        )
      }
      className="flex items-center justify-center gap-2 border rounded-md px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600"
    >
      <Download size={16} />
      Download
    </button>
  ) : (
    <div className="border rounded-md px-3 py-2 bg-gray-50 text-sm text-gray-500">
      —
    </div>
  )}
</div>

          </div>
        </RoleCard>
      ))
    )}
  </>
)}

          {paymentOutMode && (
  <>
    {paymentOutEntries.length === 0 ? (
      <Card title="Payment Out">
        <div className="text-center text-gray-500 py-10">
          No payment-out entries recorded yet.
        </div>
      </Card>
    ) : (
      paymentOutEntries.map((payment) => (
        <RoleCard
          key={payment.payment_id}
          title={`Payment Out #${payment.payment_id}`}
          icon={<CreditCard size={18} />}
          accentColor="border-t-red-500"
        >
          <div className="grid md:grid-cols-3 gap-5">

            {/* All normal fields first */}
{Object.entries(payment)
  .filter(([key]) => key !== "attachment")
  .map(([key, value]) => (
    <Field
      key={key}
      label={key.replace(/_/g, " ")}
      value={value}
      isCurrency={isCurrencyField(key)}
    />
  ))}

{/* Attachment */}
<div className="flex flex-col gap-1">
  <label className="text-xs font-medium text-gray-500">
    Attachment
  </label>

  {payment.attachment ? (
    <button
      onClick={() =>
        downloadBase64File(
          payment.attachment,
          `Payment_Out_${payment.payment_id}`,
          "application/octet-stream"
        )
      }
      className="flex items-center justify-center gap-2 border rounded-md px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600"
    >
      <Download size={16} />
      Download
    </button>
  ) : (
    <div className="border rounded-md px-3 py-2 bg-gray-50 text-sm text-gray-500">
      —
    </div>
  )}
</div>

          </div>
        </RoleCard>
      ))
    )}
  </>
)}

          
        </div>

        {/* RIGHT COLUMN */}
        {!purchaseMode && !paymentInMode && !paymentOutMode && (
        <div className="space-y-6">

          {/* ATTACHMENTS */}
          <Card title="Role Compliance Attachments">
            <label className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition block cursor-pointer">
  <Upload size={24} className="mx-auto text-gray-400" />

  <p className="mt-3 text-sm font-medium text-gray-700">
    {uploading ? "Uploading..." : "Upload Documents"}
  </p>

  <p className="text-xs text-gray-500 mt-1">
    PDF, XLSX, DOC, Images
  </p>

  <input
    type="file"
    className="hidden"
    onChange={uploadAttachment}
  />
</label>
            <div className="mt-5 space-y-3">
              {files.map((file) => (
  <div
    key={file.opfat_id}
    className="border rounded-lg p-3 flex justify-between items-center hover:bg-gray-50"
  >
    <div className="flex items-center gap-3">
      <FileText size={18} />

      <div>
        <div className="text-sm font-medium">
          {file.opfat_attachment_name}
        </div>

        <div className="text-xs text-gray-500">
          {file.opfat_attachment_type}
        </div>
      </div>
    </div>

    <button
  onClick={() =>
    downloadAttachment(
      file.opfat_id,
      file.opfat_attachment_name
    )
  }
>
  <Download size={18} className="text-blue-600" />
</button>
  </div>
))}
            </div>
          </Card>
          

          <Card title="Contact Details">
  <div className="space-y-3">
    {contacts.length > 0 ? (
      contacts.map((contact, index) => (
        <div
          key={index}
          className="border rounded-lg p-3 hover:bg-gray-50 transition"
        >
          <p className="font-medium text-blue-600">
            Name: {contact.poc_name}
          </p>

          <p className="text-xs text-gray-500 mt-1">
            Email: {contact.poc_email || "—"}
          </p>

          <p className="text-xs text-gray-500">
            Phone: {contact.poc_contact || "—"}
          </p>
        </div>
      ))
    ) : (
      <div className="text-sm text-gray-500">
        No contacts available.
      </div>
    )}
  </div>
</Card>

          {/* NOTES */}
          <Card title="Internal Team Notes">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Write a note..."
              className="w-full border border-gray-300 rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={4}
            />
            <button onClick={addNote} className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md text-sm font-medium transition">
              Post Note
            </button>
            <div className="mt-4 space-y-3 max-h-64 overflow-auto">
              {notes.map((note) => (
  <div
    key={note.opfnts_id}
    className="border border-gray-200 rounded-lg p-3 bg-gray-50"
  >
    <div className="flex justify-between">
      <span className="text-xs font-semibold text-gray-700">
        {note.usr_name}
      </span>

      <span className="text-[11px] text-gray-500">
        writen by: {note.usr_name}
      </span>
    </div>

    <p className="mt-2 text-sm text-gray-600">
      {note.opfnts_note}
    </p>
  </div>
))}
            </div>
          </Card>

          

          {/* AUDIT */}
          
        </div>)}
      </div>
    </div>
  );
}

/* ---------------- CARD ---------------- */
function Card({ title, action, children }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        {action && <div>{action}</div>}
      </div>
      {children}
    </div>
  );
}

/* ---------------- ROLE CARD ---------------- */
// MODIFIED: Accepts an optional `action` element to render dynamic header components (like Edit buttons)
function RoleCard({ title, icon, accentColor, action, children }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm border-t-4 ${accentColor}`}>
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-gray-800">{title}</h3>
        </div>
        {action && <div>{action}</div>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

/* ---------------- SHIPPING ADDRESS (READ-ONLY) ---------------- */
function ShippingAddressView({ addr }) {
  const parsed = splitPackedAddress(addr.address);
  const rows = [
    { label: "Name", value: parsed.name },
    { label: "Contact", value: parsed.contact },
    { label: "Email", value: parsed.email },
    { label: "Address", value: parsed.addressLine },
    { label: "GST", value: parsed.gst },
  ].filter((r) => r.value);

  return (
    <div className="border rounded-lg p-3 mb-3">
      <p className="font-medium text-gray-800">{addr.label || "Untitled"}</p>
      {rows.length > 0 ? (
        <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {rows.map((r) => (
            <div key={r.label} className="flex text-sm">
              <dt className="text-gray-500 w-16 shrink-0">{r.label}:</dt>
              <dd className="text-gray-700 break-words">{r.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-sm text-gray-500 mt-1">No details added.</p>
      )}
    </div>
  );
}

/* ---------------- SHIPPING ADDRESS (EDITABLE) ---------------- */
function ShippingAddressEditForm({ draft, onChange }) {
  const inputClass =
    "border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200";

  return (
    <div className="border border-blue-200 rounded-lg p-3 mb-3 space-y-2 bg-blue-50/40">
      <input
        value={draft.label}
        onChange={(e) => onChange("label", e.target.value)}
        placeholder="Label"
        className={`w-full font-medium ${inputClass}`}
      />
      <div className="grid sm:grid-cols-2 gap-2">
        <input
          value={draft.name}
          onChange={(e) => onChange("name", e.target.value)}
          placeholder="Name"
          className={inputClass}
        />
        <input
          value={draft.contact}
          onChange={(e) => onChange("contact", e.target.value)}
          placeholder="Contact"
          className={inputClass}
        />
        <input
          type="email"
          value={draft.email}
          onChange={(e) => onChange("email", e.target.value)}
          placeholder="Email"
          className={inputClass}
        />
        <input
          value={draft.gst}
          onChange={(e) => onChange("gst", e.target.value)}
          placeholder="GST (optional)"
          className={inputClass}
        />
      </div>
      <textarea
        value={draft.addressLine}
        onChange={(e) => onChange("addressLine", e.target.value)}
        placeholder="Address"
        rows={2}
        className={`w-full resize-none ${inputClass}`}
      />
    </div>
  );
}

/* ---------------- FIELD ---------------- */
function Field({ label, value, editable = false, onChange, monospace = false, className = "", isCurrency = false, large = false }) {
  const displayValue = isCurrency ? formatIndianCurrency(value) : value;

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-xs font-medium text-gray-500">{label}</label>
      {editable ? (
        <div className={isCurrency ? "relative" : undefined}>
          {isCurrency && (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
              ₹
            </span>
          )}
          {large ? (
            <textarea
              value={displayValue}
              onChange={(e) =>
                onChange && onChange(isCurrency ? unformatCurrency(e.target.value) : e.target.value)
              }
              rows={3}
              className={`w-full border rounded-md py-2 px-3 text-sm border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none resize-y ${
                monospace ? "font-mono" : ""
              }`}
            />
          ) : (
            <input
              value={displayValue}
              onChange={(e) =>
                onChange && onChange(isCurrency ? unformatCurrency(e.target.value) : e.target.value)
              }
              className={`w-full border rounded-md py-2 text-sm border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none ${
                isCurrency ? "pl-7 pr-3" : "px-3"
              } ${monospace ? "font-mono" : ""}`}
            />
          )}
        </div>
      ) : (
        <div
          className={`border rounded-md px-3 py-2 bg-gray-50 text-sm text-gray-700 break-words ${
            large ? "min-h-[68px] whitespace-pre-wrap leading-relaxed" : ""
          } ${monospace ? "font-mono" : ""}`}
        >
          {displayValue ? `${isCurrency ? "₹" : ""}${displayValue}` : "—"}
        </div>
      )}
    </div>
  );
}